/**
 * UpdateProtocol.js
 * 
 * Service for applying precise updates to code files.
 * Implements surgical code modifications using line operations.
 */

class UpdateProtocol {
  constructor(storageManager) {
    this.storage = storageManager;
  }
  
  /**
   * Apply an update operation to a file
   * @param {string} projectId - Project ID
   * @param {Object} updateOperation - Update operation
   * @returns {Promise<Object>} Update result
   */
  async applyUpdate(projectId, updateOperation) {
    // Get file path and operations
    const { p: filePath, o: operations } = updateOperation;
    
    try {
      // Get file by path
      const file = await this.getFileByPath(projectId, filePath);
      if (!file) {
        return {
          success: false,
          error: `File not found: ${filePath}`
        };
      }
      
      // Get current content
      const currentContentObj = await this.storage.getFileContent(file.id);
      if (!currentContentObj) {
        return {
          success: false,
          error: `File content not found for ${filePath}`
        };
      }
      
      const currentContent = currentContentObj.content;
      
      // Apply operations
      let updatedContent = currentContent;
      const appliedOps = [];
      
      for (const op of operations) {
        try {
          switch (op.t) {
            case 'R': // Replace lines
              updatedContent = this.applyReplaceOperation(updatedContent, op);
              appliedOps.push({ type: 'replace', lines: `${op.s}-${op.e}` });
              break;
            case 'F': // Function replacement
              updatedContent = this.applyFunctionOperation(updatedContent, op, file.language);
              appliedOps.push({ type: 'function', name: op.n });
              break;
            case 'C': // Class method replacement
              updatedContent = this.applyClassMethodOperation(updatedContent, op, file.language);
              appliedOps.push({ type: 'method', class: op.n, method: op.m });
              break;
            case 'I': // Import operation
              updatedContent = this.applyImportOperation(updatedContent, op, file.language);
              appliedOps.push({ type: 'import', action: op.a });
              break;
            default:
              console.warn(`Unknown operation type: ${op.t}`);
          }
        } catch (error) {
          return {
            success: false,
            error: `Failed to apply operation: ${error.message}`,
            operation: op
          };
        }
      }
      
      // Validate updated content
      if (!this.validateUpdateResult(updatedContent, file.language)) {
        return {
          success: false,
          error: `Invalid update result for ${filePath}`
        };
      }
      
      // Store new version
      const newVersion = currentContentObj.version + 1;
      const contentId = await this.storage.saveFileContent(
        file.id,
        updatedContent,
        newVersion
      );
      
      // Update file metadata
      file.currentVersionId = contentId;
      file.actualLoc = this.countLinesOfCode(updatedContent);
      file.exports = this.extractExports(updatedContent, file.language);
      file.imports = this.extractImports(updatedContent, file.language);
      
      await this.storage.saveFile(file);
      
      return {
        success: true,
        filePath,
        newVersionId: contentId,
        fileId: file.id,
        operations: appliedOps
      };
    } catch (error) {
      console.error(`Error applying update to ${filePath}:`, error);
      return {
        success: false,
        error: `Update failed: ${error.message}`
      };
    }
  }
  
  /**
   * Apply a replace operation
   * @param {string} content - File content
   * @param {Object} operation - Replace operation
   * @returns {string} Updated content
   */
  applyReplaceOperation(content, operation) {
    const lines = content.split('\n');
    
    // Validate line range
    if (operation.s < 1 || operation.e > lines.length || operation.s > operation.e) {
      // Try fuzzy matching if line numbers don't match exactly
      const fuzzyMatch = this.findFuzzyLineMatch(content, operation);
      if (fuzzyMatch) {
        operation.s = fuzzyMatch.start;
        operation.e = fuzzyMatch.end;
      } else {
        throw new Error(`Invalid line range: ${operation.s}-${operation.e} (max: ${lines.length})`);
      }
    }
    
    // Replace lines
    const newLines = operation.c.split('\n');
    const result = [
      ...lines.slice(0, operation.s - 1),
      ...newLines,
      ...lines.slice(operation.e)
    ];
    
    return result.join('\n');
  }
  
  /**
   * Apply a function replacement operation
   * @param {string} content - File content
   * @param {Object} operation - Function operation
   * @param {string} language - File language
   * @returns {string} Updated content
   */
  applyFunctionOperation(content, operation, language) {
    // Find function in the file
    const functionInfo = this.findFunction(content, operation.n, language);
    
    if (!functionInfo) {
      // Try fuzzy function name matching
      const fuzzyMatch = this.findFuzzyFunctionMatch(content, operation.n, language);
      
      if (fuzzyMatch) {
        return this.replaceFunctionAtPosition(
          content, 
          fuzzyMatch.start, 
          fuzzyMatch.end, 
          operation.c
        );
      }
      
      throw new Error(`Function not found: ${operation.n}`);
    }
    
    // Replace function
    return this.replaceFunctionAtPosition(
      content, 
      functionInfo.start, 
      functionInfo.end, 
      operation.c
    );
  }
  
  /**
   * Find a function in content
   * @param {string} content - File content
   * @param {string} functionName - Function name
   * @param {string} language - File language
   * @returns {Object|null} Function info or null
   */
  findFunction(content, functionName, language) {
    // Basic function detection - should be enhanced with proper parsing
    if (language === 'javascript' || language === 'js') {
      // Match function declarations
      const functionRegex = new RegExp(
        `(function\\s+${functionName}\\s*\\([^)]*\\)\\s*{[\\s\\S]*?^})`,'m'
      );
      
      // Match arrow functions and function expressions
      const arrowFunctionRegex = new RegExp(
        `(const|let|var)\\s+${functionName}\\s*=\\s*(async\\s*)?\\([^)]*\\)\\s*=>\\s*{[\\s\\S]*?^}`,'m'
      );

      // Match class methods
      const methodFunctionRegex = new RegExp(
        `(\\s+${functionName}\\s*\\([^)]*\\)\\s*{[\\s\\S]*?^\\s+})`,'m'
      );
      
      // Try to match any of these patterns
      const match = content.match(functionRegex) || 
                   content.match(arrowFunctionRegex) ||
                   content.match(methodFunctionRegex);
      
      if (match) {
        const functionContent = match[0];
        const start = content.indexOf(functionContent);
        const end = start + functionContent.length;
        
        return {
          name: functionName,
          content: functionContent,
          start,
          end
        };
      }
    }
    
    return null;
  }
  
  /**
   * Replace function at position
   * @param {string} content - File content
   * @param {number} start - Start position
   * @param {number} end - End position
   * @param {string} newFunction - New function code
   * @returns {string} Updated content
   */
  replaceFunctionAtPosition(content, start, end, newFunction) {
    return content.substring(0, start) + newFunction + content.substring(end);
  }
  
  /**
   * Find a fuzzy line match for a replace operation
   * @param {string} content - File content
   * @param {Object} operation - Replace operation
   * @returns {Object|null} Match position or null
   */
  findFuzzyLineMatch(content, operation) {
    // Extract a context block from the operation
    const operationLines = operation.c.split('\n');
    const contextSize = Math.min(3, operationLines.length);
    const contextLines = operationLines.slice(0, contextSize).join('\n');
    
    // Search for this context in the content
    const contentLines = content.split('\n');
    
    for (let i = 0; i < contentLines.length - contextSize + 1; i++) {
      const candidateContext = contentLines.slice(i, i + contextSize).join('\n');
      const similarity = this.calculateStringSimilarity(contextLines, candidateContext);
      
      if (similarity > 0.8) { // 80% similarity threshold
        // Found a likely match
        const expectedLength = operation.e - operation.s + 1;
        return {
          start: i + 1,
          end: i + expectedLength
        };
      }
    }
    
    return null;
  }
  
  /**
   * Calculate string similarity (Jaccard index)
   * @param {string} str1 - First string
   * @param {string} str2 - Second string
   * @returns {number} Similarity score (0-1)
   */
  calculateStringSimilarity(str1, str2) {
    // Simple Jaccard similarity for quick fuzzy matching
    const set1 = new Set(str1.split(/\s+/));
    const set2 = new Set(str2.split(/\s+/));
    
    const intersection = new Set([...set1].filter(x => set2.has(x)));
    const union = new Set([...set1, ...set2]);
    
    return intersection.size / union.size;
  }
  
  /**
   * Find a fuzzy function match
   * @param {string} content - File content
   * @param {string} functionName - Function name
   * @param {string} language - File language
   * @returns {Object|null} Match position or null
   */
  findFuzzyFunctionMatch(content, functionName, language) {
    // Try to find functions with similar names
    const lines = content.split('\n');
    const candidates = [];
    
    // Find candidate function declarations
    if (language === 'javascript' || language === 'js') {
      // Look for function declarations, arrow functions, and function expressions
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        
        if (line.match(/function\s+(\w+)/)) {
          const match = line.match(/function\s+(\w+)/);
          candidates.push({
            name: match[1],
            line: i + 1,
            similarity: this.calculateStringSimilarity(functionName, match[1])
          });
        }
        
        if (line.match(/(const|let|var)\s+(\w+)\s*=\s*(async\s*)?\(/)) {
          const match = line.match(/(const|let|var)\s+(\w+)\s*=/);
          candidates.push({
            name: match[2],
            line: i + 1,
            similarity: this.calculateStringSimilarity(functionName, match[2])
          });
        }

        // Class methods
        if (line.match(/\s+(\w+)\s*\(/)) {
          const match = line.match(/\s+(\w+)\s*\(/);
          candidates.push({
            name: match[1],
            line: i + 1,
            similarity: this.calculateStringSimilarity(functionName, match[1])
          });
        }
      }
    }
    
    // Find best match
    const bestMatch = candidates
      .filter(c => c.similarity > 0.5) // At least 50% similar
      .sort((a, b) => b.similarity - a.similarity)[0];
    
    if (bestMatch) {
      // Find function boundaries
      let start = bestMatch.line - 1;
      let openBraces = 0;
      let foundFirstBrace = false;
      
      for (let i = start; i < lines.length; i++) {
        const line = lines[i];
        
        // Count opening and closing braces
        for (const char of line) {
          if (char === '{') {
            openBraces++;
            foundFirstBrace = true;
          } else if (char === '}') {
            openBraces--;
          }
        }
        
        // Function ends when braces are balanced
        if (foundFirstBrace && openBraces === 0) {
          return {
            name: bestMatch.name,
            start,
            end: i + 1
          };
        }
      }
    }
    
    return null;
  }
  
  /**
   * Apply a class method operation
   * @param {string} content - File content
   * @param {Object} operation - Method operation
   * @param {string} language - File language
   * @returns {string} Updated content
   */
  applyClassMethodOperation(content, operation, language) {
    // Find class in the file
    const classInfo = this.findClass(content, operation.n, language);
    
    if (!classInfo) {
      throw new Error(`Class not found: ${operation.n}`);
    }
    
    // Find method within class
    const methodInfo = this.findMethodInClass(classInfo.content, operation.m, language);
    
    if (!methodInfo) {
      throw new Error(`Method ${operation.m} not found in class ${operation.n}`);
    }
    
    // Replace method
    const methodStart = classInfo.start + methodInfo.start;
    const methodEnd = classInfo.start + methodInfo.end;
    
    return content.substring(0, methodStart) + operation.c + content.substring(methodEnd);
  }
  
  /**
   * Find a class in content
   * @param {string} content - File content
   * @param {string} className - Class name
   * @param {string} language - File language
   * @returns {Object|null} Class info or null
   */
  findClass(content, className, language) {
    // Basic class detection - should be enhanced with proper parsing
    if (language === 'javascript' || language === 'js') {
      const classRegex = new RegExp(`class\\s+${className}\\s*{[\\s\\S]*?^}`, 'm');
      const match = content.match(classRegex);
      
      if (match) {
        const classContent = match[0];
        const start = content.indexOf(classContent);
        const end = start + classContent.length;
        
        return {
          name: className,
          content: classContent,
          start,
          end
        };
      }
    }
    
    return null;
  }
  
  /**
   * Find a method in a class
   * @param {string} classContent - Class content
   * @param {string} methodName - Method name
   * @param {string} language - File language
   * @returns {Object|null} Method info or null
   */
  findMethodInClass(classContent, methodName, language) {
    // Extract class body (remove class declaration line)
    const classBody = classContent.substring(classContent.indexOf('{') + 1, classContent.lastIndexOf('}'));
    
    // Find method
    if (language === 'javascript' || language === 'js') {
      const methodRegex = new RegExp(`\\s*(async\\s*)?${methodName}\\s*\\([^)]*\\)\\s*{[\\s\\S]*?^\\s*}`, 'm');
      const match = classBody.match(methodRegex);
      
      if (match) {
        const methodContent = match[0];
        const start = classBody.indexOf(methodContent) + classContent.indexOf('{') + 1;
        const end = start + methodContent.length;
        
        return {
          name: methodName,
          content: methodContent,
          start,
          end
        };
      }
    }
    
    return null;
  }
  
  /**
   * Apply an import operation
   * @param {string} content - File content
   * @param {Object} operation - Import operation
   * @param {string} language - File language
   * @returns {string} Updated content
   */
  applyImportOperation(content, operation, language) {
    if (language !== 'javascript' && language !== 'js') {
      throw new Error(`Import operations not supported for language: ${language}`);
    }
    
    switch (operation.a) {
      case 'A': // Add import
        return this.addImport(content, operation.c);
      case 'R': // Replace import
        return this.replaceImport(content, operation.o, operation.c);
      case 'D': // Delete import
        return this.deleteImport(content, operation.c);
      default:
        throw new Error(`Unknown import operation: ${operation.a}`);
    }
  }
  
  /**
   * Add an import statement
   * @param {string} content - File content
   * @param {string} importStatement - Import statement
   * @returns {string} Updated content
   */
  addImport(content, importStatement) {
    // Find the last import in the file
    const lines = content.split('\n');
    let lastImportIndex = -1;
    
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].trim().startsWith('import ') || 
          (lines[i].trim().startsWith('const ') && lines[i].includes('require('))) {
        lastImportIndex = i;
      }
    }
    
    if (lastImportIndex >= 0) {
      // Add after the last import
      lines.splice(lastImportIndex + 1, 0, importStatement);
    } else {
      // Add at the beginning of the file
      lines.unshift(importStatement);
    }
    
    return lines.join('\n');
  }
  
  /**
   * Replace an import statement
   * @param {string} content - File content
   * @param {string} oldImport - Old import statement
   * @param {string} newImport - New import statement
   * @returns {string} Updated content
   */
  replaceImport(content, oldImport, newImport) {
    return content.replace(oldImport, newImport);
  }
  
  /**
   * Delete an import statement
   * @param {string} content - File content
   * @param {string} importStatement - Import statement
   * @returns {string} Updated content
   */
  deleteImport(content, importStatement) {
    const lines = content.split('\n');
    const index = lines.findIndex(line => line.trim() === importStatement.trim());
    
    if (index >= 0) {
      lines.splice(index, 1);
    }
    
    return lines.join('\n');
  }
  
  /**
   * Validate update result
   * @param {string} content - Updated content
   * @param {string} language - File language
   * @returns {boolean} Validation result
   */
  validateUpdateResult(content, language) {
    // Simple validation to ensure the content is still valid code
    // Should be enhanced with proper parsing
    
    // Check for balanced braces
    let braces = 0;
    
    for (const char of content) {
      if (char === '{') braces++;
      if (char === '}') braces--;
      
      if (braces < 0) return false; // Unbalanced closing brace
    }
    
    return braces === 0; // All braces should be balanced
  }
  
  /**
   * Get a file by path
   * @param {string} projectId - Project ID
   * @param {string} filePath - File path
   * @returns {Promise<Object>} File object
   */
  async getFileByPath(projectId, filePath) {
    return await this.storage.getFileByPath(projectId, filePath);
  }
  
  /**
   * Count lines of code
   * @param {string} content - File content
   * @returns {number} Line count
   */
  countLinesOfCode(content) {
    return content.split('\n').length;
  }
  
  /**
   * Extract exports from content
   * @param {string} content - File content
   * @param {string} language - File language
   * @returns {Array} Exports array
   */
  extractExports(content, language) {
    // Simple export detection - should be enhanced with proper parsing
    const exports = [];
    
    if (language === 'javascript' || language === 'js') {
      // Match module.exports and named exports
      const moduleExports = /module\.exports\s*=\s*{([^}]*)}/g.exec(content);
      if (moduleExports) {
        const exportItems = moduleExports[1].split(',');
        for (const item of exportItems) {
          const trimmed = item.trim();
          if (trimmed) exports.push(trimmed);
        }
      }
      
      // Match ES6 exports
      const exportMatches = content.match(/export\s+(const|let|var|function|class)\s+(\w+)/g);
      if (exportMatches) {
        for (const match of exportMatches) {
          const parts = match.split(/\s+/);
          if (parts.length >= 3) exports.push(parts[2]);
        }
      }
      
      // Match default export
      const defaultExport = /export\s+default\s+(\w+)/g.exec(content);
      if (defaultExport && defaultExport[1]) {
        exports.push(`default (${defaultExport[1]})`);
      }
    }
    
    return exports;
  }
  
  /**
   * Extract imports from content
   * @param {string} content - File content
   * @param {string} language - File language
   * @returns {Array} Imports array
   */
  extractImports(content, language) {
    // Simple import detection - should be enhanced with proper parsing
    const imports = [];
    
    if (language === 'javascript' || language === 'js') {
      // Match require statements
      const requireMatches = content.match(/require\(['"]([^'"]+)['"]\)/g);
      if (requireMatches) {
        for (const match of requireMatches) {
          const path = match.match(/require\(['"]([^'"]+)['"]\)/)[1];
          imports.push(path);
        }
      }
      
      // Match ES6 imports
      const importMatches = content.match(/import\s+.*?from\s+['"]([^'"]+)['"]/g);
      if (importMatches) {
        for (const match of importMatches) {
          const path = match.match(/from\s+['"]([^'"]+)['"]/)[1];
          imports.push(path);
        }
      }
    }
    
    return imports;
  }
}

export default UpdateProtocol;
